import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiPlus, FiX, FiCheck } from 'react-icons/fi';
import api from '@/services/api';
import ImageUpload from '@/components/common/ImageUpload';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const CATEGORIES = [
  { id: 'smartphones', name: 'Smartphones' },
  { id: 'laptops', name: 'Laptops' },
  { id: 'tablets', name: 'Tablets' },
  { id: 'audio', name: 'Áudio' },
  { id: 'games', name: 'Games' },
  { id: 'cameras', name: 'Câmeras' },
  { id: 'tvs', name: 'TVs' },
  { id: 'accessories', name: 'Acessórios' },
  { id: 'vestuario', name: 'Vestuário' },
  { id: 'calcados', name: 'Calçados' },
  { id: 'casa', name: 'Casa & Decoração' }
];

const COLORS = [
  "Preto", "Branco", "Azul", "Vermelho", "Verde", "Amarelo", 
  "Rosa", "Roxo", "Laranja", "Cinza", "Marrom", "Bege"
];

const SIZES_CLOTHING = ["PP", "P", "M", "G", "GG", "XG", "XXG"];
const SIZES_SHOES = ["34", "35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45"];

// Categorias que permitem variações
const CATEGORIES_WITH_VARIATIONS = ["vestuario", "calcados"];

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    price: '', 
    stock: '', 
    category: 'smartphones', 
    condition: 'novo',
    has_variations: false,
    available_colors: [],
    available_sizes: []
  });

  const showVariations = CATEGORIES_WITH_VARIATIONS.includes(formData.category);
  const sizeOptions = formData.category === 'calcados' ? SIZES_SHOES : SIZES_CLOTHING;

  useEffect(() => { if (isEditing) loadProduct(); }, [id]);

  // Resetar variações quando mudar categoria
  useEffect(() => {
    if (!showVariations) {
      setFormData(prev => ({
        ...prev,
        has_variations: false,
        available_colors: [],
        available_sizes: []
      }));
    }
  }, [formData.category, showVariations]);

  const loadProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      const p = response.data;
      setFormData({ 
        name: p.name, 
        description: p.description, 
        price: p.price.toString(), 
        stock: p.stock.toString(), 
        category: p.category, 
        condition: p.condition,
        has_variations: p.has_variations || false,
        available_colors: p.available_colors || [],
        available_sizes: p.available_sizes || []
      });
      setImages(p.photos || []);
    } catch (error) {
      setError('Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  const toggleColor = (color) => {
    setFormData(prev => ({
      ...prev,
      available_colors: prev.available_colors.includes(color)
        ? prev.available_colors.filter(c => c !== color)
        : [...prev.available_colors, color]
    }));
  };

  const toggleSize = (size) => {
    setFormData(prev => ({
      ...prev,
      available_sizes: prev.available_sizes.includes(size)
        ? prev.available_sizes.filter(s => s !== size)
        : [...prev.available_sizes, size]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const photoUrls = images.map(img => typeof img === 'string' ? img : img.url);
      const data = { 
        ...formData, 
        price: parseFloat(formData.price), 
        stock: parseInt(formData.stock), 
        photos: photoUrls,
        has_variations: formData.has_variations && showVariations
      };
      
      let productId;
      if (isEditing) {
        await api.put(`/products/${id}`, data);
        productId = id;
      } else {
        const response = await api.post('/products', data);
        productId = response.data.id;
      }
      
      navigate(`/lojista/produto/${productId}/preview`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><LoadingSpinner /></div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="product-form">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full"><FiArrowLeft className="text-xl" /></button>
          <h1 className="font-semibold text-gray-800">{isEditing ? 'Editar' : 'Novo'} Produto</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-xl">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Fotos */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold mb-4">Fotos</h3>
            <ImageUpload images={images} setImages={setImages} maxImages={5} />
          </div>

          {/* Informações Básicas */}
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="font-semibold">Informações</h3>
            <input 
              type="text" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              className="w-full px-4 py-3 border rounded-xl" 
              placeholder="Nome do produto" 
              required 
              data-testid="product-name"
            />
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              className="w-full px-4 py-3 border rounded-xl resize-none" 
              placeholder="Descrição detalhada do produto" 
              rows={4} 
              required 
              data-testid="product-description"
            />
            <div className="grid grid-cols-2 gap-4">
              <input 
                type="number" 
                step="0.01" 
                value={formData.price} 
                onChange={(e) => setFormData({ ...formData, price: e.target.value })} 
                className="px-4 py-3 border rounded-xl" 
                placeholder="Preço (R$)" 
                required 
                data-testid="product-price"
              />
              <input 
                type="number" 
                value={formData.stock} 
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })} 
                className="px-4 py-3 border rounded-xl" 
                placeholder="Estoque" 
                required 
                data-testid="product-stock"
              />
            </div>
          </div>

          {/* Categoria e Condição */}
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="font-semibold">Categoria</h3>
            <select 
              value={formData.category} 
              onChange={(e) => setFormData({ ...formData, category: e.target.value })} 
              className="w-full px-4 py-3 border rounded-xl bg-white"
              data-testid="product-category"
            >
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex gap-4">
              <button 
                type="button" 
                onClick={() => setFormData({ ...formData, condition: 'novo' })} 
                className={`flex-1 py-3 rounded-xl border-2 font-medium ${formData.condition === 'novo' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200'}`}
              >
                ✨ Novo
              </button>
              <button 
                type="button" 
                onClick={() => setFormData({ ...formData, condition: 'usado' })} 
                className={`flex-1 py-3 rounded-xl border-2 font-medium ${formData.condition === 'usado' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' : 'border-gray-200'}`}
              >
                ♻️ Usado
              </button>
            </div>
          </div>

          {/* Variações - Só aparece para categorias de vestuário e calçados */}
          {showVariations && (
            <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Variações do Produto</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_variations}
                    onChange={(e) => setFormData({ ...formData, has_variations: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Produto tem variações</span>
                </label>
              </div>

              {formData.has_variations && (
                <>
                  {/* Cores */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Cores disponíveis:</p>
                    <div className="flex flex-wrap gap-2">
                      {COLORS.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => toggleColor(color)}
                          className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            formData.available_colors.includes(color)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {formData.available_colors.includes(color) && <FiCheck className="inline mr-1" size={14} />}
                          {color}
                        </button>
                      ))}
                    </div>
                    {formData.available_colors.length > 0 && (
                      <p className="text-xs text-green-600 mt-2">
                        ✓ {formData.available_colors.length} cor(es) selecionada(s)
                      </p>
                    )}
                  </div>

                  {/* Tamanhos */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Tamanhos disponíveis ({formData.category === 'calcados' ? 'Numeração' : 'Roupa'}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {sizeOptions.map(size => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => toggleSize(size)}
                          className={`w-12 h-12 rounded-lg border-2 text-sm font-bold transition-all ${
                            formData.available_sizes.includes(size)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    {formData.available_sizes.length > 0 && (
                      <p className="text-xs text-green-600 mt-2">
                        ✓ {formData.available_sizes.length} tamanho(s) selecionado(s)
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      💡 <strong>Dica:</strong> Com variações ativadas, o cliente poderá escolher a cor e tamanho no momento da compra.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
        <button 
          onClick={handleSubmit} 
          disabled={submitting} 
          className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="save-product-btn"
        >
          <FiSave /> {submitting ? 'Salvando...' : 'Salvar Produto'}
        </button>
      </div>
    </div>
  );
};

export default ProductForm;
